// src/marketing/budget/useDailyExpenses.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { DailyExpense } from '../../lib/types'

const expenseKeys = {
  all: ['daily-expenses'] as const,
  list: (appId: string) => ['daily-expenses', 'list', appId] as const,
}

export function useDailyExpenses(appId: string | null) {
  return useQuery<DailyExpense[], Error>({
    queryKey: expenseKeys.list(appId ?? ''),
    queryFn: () =>
      api
        .get<{ expenses: DailyExpense[] }>(`/api/daily-expenses?appId=${appId}`)
        .then((r) => r.expenses),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 2,
  })
}

export type CreateExpenseInput = {
  appId: string
  expenseDate: string
  amount: number
  currency?: 'EUR' | undefined
  category: string
  platform?: string | null | undefined
  contentId?: string | null | undefined
  dealId?: string | null | undefined
  note?: string | null | undefined
}

export type UpdateExpenseInput = Omit<Partial<CreateExpenseInput>, 'appId'>

export function useCreateDailyExpense() {
  const queryClient = useQueryClient()
  return useMutation<DailyExpense, Error, CreateExpenseInput>({
    mutationFn: (body) =>
      api.post<{ expense: DailyExpense }>('/api/daily-expenses', body).then((r) => r.expense),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: expenseKeys.all })
    },
  })
}

export function useUpdateDailyExpense(id: string) {
  const queryClient = useQueryClient()
  return useMutation<DailyExpense, Error, UpdateExpenseInput>({
    mutationFn: (body) =>
      api.put<{ expense: DailyExpense }>(`/api/daily-expenses/${id}`, body).then((r) => r.expense),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: expenseKeys.all })
    },
  })
}

export function useDeleteDailyExpense() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.del<{ ok: boolean }>(`/api/daily-expenses/${id}`).then(() => undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: expenseKeys.all })
    },
  })
}
